/**
 * Pointer-based drag-reorder for columns and rows (mouse + touch).
 *
 * A grip (`DragGrip`) starts a gesture tracked on `window` with pointer capture
 * so touch scrolling cannot steal the drag. Columns slide into place while the pointer
 * moves, and the reorder callback fires once on release. Losing pointer capture
 * during that move is not a drop. Rows live-reorder the same way.
 * Pointer events instead of HTML5 DnD: DataGrid headers live inside a
 * `draggable=false` container and native drag gestures don't start reliably
 * there (and HTML5 DnD does not work on touch).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode, type RefObject } from 'react';
import { Box, Portal, type SxProps, type Theme } from '@mui/material';
import { gridClasses, type GridColDef, type GridValidRowModel } from '@mui/x-data-grid';
import { DragIndicatorIcon } from '../../icons';

export type ReorderHitInput = {
  /** Pointer axis (clientX for columns, clientY for rows). */
  pointer: number;
  targetStart: number;
  targetEnd: number;
  fromIndex: number;
  toIndex: number;
  /**
   * How far into the target (0–1) the pointer must travel in the drag direction
   * before a swap commits. Default `0.2` (20% of the column/row extent).
   */
  crossRatio?: number;
};

/**
 * Directional hysteresis for live-reorder hit testing: true when the pointer has
 * crossed `crossRatio` of the target in the drag direction, so near-edge hover
 * alone does not flip order back and forth (the classic edge flicker).
 */
export function shouldReorderToward({ pointer, targetStart, targetEnd, fromIndex, toIndex, crossRatio = 0.2 }: ReorderHitInput): boolean {
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return false;
  const span = targetEnd - targetStart;
  if (span <= 0) return false;
  const ratio = Math.min(1, Math.max(0, crossRatio));

  if (fromIndex < toIndex) {
    // Dragging right / down — wait until 20% into the target from its near edge.
    return pointer >= targetStart + span * ratio;
  }
  // Dragging left / up — mirrored from the far edge.
  return pointer <= targetEnd - span * ratio;
}

/**
 * Minimum ms between live column swaps (extra debounce on top of hitbox hysteresis,
 * because unequal column widths can re-trigger a swap right after one commits).
 * Row drags skip this: rows share one height, so hysteresis alone is flicker-free.
 */
export const REORDER_COOLDOWN_MS = 300;

/** What the user picked up: a column `field` or a stringified row id. */
export type DragKind = 'column' | 'row';

/** Mutable state of one drag gesture. `changed` gates the commit callback on release. */
type DragSession = { kind: DragKind; key: string; changed: boolean };

/**
 * The column `field` / row id under the pointer, plus the hit element's box for hysteresis.
 * Resolved with `elementFromPoint` because the pointer can be over any part of a header
 * or row — padding, the sort icon, the menu icon — not just its content.
 */
function dragTargetHit(container: HTMLElement, kind: DragKind, x: number, y: number): { key: string; rect: DOMRect } | null {
  const selector = kind === 'column' ? `.${gridClasses.columnHeader}[data-field]` : `.${gridClasses.row}[data-id]`;
  const hit = document.elementFromPoint(x, y)?.closest<HTMLElement>(selector);
  if (!hit || !container.contains(hit)) return null;
  const key = (kind === 'column' ? hit.dataset.field : hit.dataset.id) ?? null;
  if (!key) return null;
  return { key, rect: hit.getBoundingClientRect() };
}

/** Floating drag preview offset so the card sits just below-right of the pointer. */
function ghostTransform(x: number, y: number): string {
  return `translate(${x + 12}px, ${y + 12}px)`;
}

function reorder<T>(list: T[], from: number, to: number): T[] {
  if (from < 0 || to < 0 || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item as T);
  return next;
}

export type DragGripProps = {
  label: string;
  iconSize: number;
  onGripPointerDown: (event: ReactPointerEvent<HTMLElement>) => void;
};

/**
 * Grip icon that starts a pointer-based drag, so header clicks (sort / search)
 * stay clickable.
 */
export function DragGrip({ label, iconSize, onGripPointerDown }: DragGripProps) {
  return (
    <Box
      component="span"
      aria-label={label}
      onPointerDown={onGripPointerDown}
      onClick={(event) => event.stopPropagation()}
      sx={{
        display: 'inline-flex',
        color: 'text.disabled',
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <DragIndicatorIcon sx={{ fontSize: iconSize }} />
    </Box>
  );
}

export type UseDragReorderOptions<R extends GridValidRowModel> = {
  containerRef: RefObject<HTMLElement | null>;
  /** Column defs in parent order — drives `columnOrder` and keeps it in sync on add/remove. */
  columns: ReadonlyArray<Pick<GridColDef<R>, 'field'>>;
  rows: readonly R[] | undefined;
  onColumnsReorder?: (fields: string[]) => void;
  onRowsReorder?: (rows: R[]) => void;
};

export type UseDragReorderResult<R extends GridValidRowModel> = {
  /** Current field order (live-updated during a column drag). */
  columnOrder: string[];
  /** Row list owned locally while row dragging is enabled. */
  localRows: R[];
  dragging: boolean;
  startDrag: (event: ReactPointerEvent<HTMLElement>, kind: DragKind, key: string, label: string) => void;
  /** Fades the dragged column / row while it stays live in the table. */
  dragSourceSx: SxProps<Theme> | undefined;
  /** Floating drag preview, rendered next to the grid. */
  ghost: ReactNode;
};

export function useDragReorder<R extends GridValidRowModel>({
  containerRef,
  columns,
  rows,
  onColumnsReorder,
  onRowsReorder,
}: UseDragReorderOptions<R>): UseDragReorderResult<R> {
  const [columnOrder, setColumnOrder] = useState<string[]>(() => columns.map((c) => c.field));
  const [localRows, setLocalRows] = useState<R[]>(() => (rows ?? []) as R[]);
  const [dragGhost, setDragGhost] = useState<{ kind: DragKind; key: string; label: string } | null>(null);
  const dragSession = useRef<DragSession | null>(null);
  const ghostRef = useRef<HTMLElement | null>(null);
  const ghostPosition = useRef({ x: 0, y: 0 });
  // Latest order/rows for the window pointer listeners, which outlive a single render.
  const columnOrderRef = useRef(columnOrder);
  const localRowsRef = useRef(localRows);
  useEffect(() => {
    columnOrderRef.current = columnOrder;
  }, [columnOrder]);
  useEffect(() => {
    localRowsRef.current = localRows;
  }, [localRows]);

  // Reset local rows when the parent hands over a new row list
  // (state adjustment during render — https://react.dev/learn/you-might-not-need-an-effect).
  const [prevRows, setPrevRows] = useState(rows);
  if (prevRows !== rows) {
    setPrevRows(rows);
    setLocalRows((rows ?? []) as R[]);
  }

  // Keep order in sync when parent adds/removes columns.
  const [prevColumns, setPrevColumns] = useState(columns);
  if (prevColumns !== columns) {
    setPrevColumns(columns);
    const fields = columns.map((c) => c.field);
    const kept = columnOrder.filter((f) => fields.includes(f));
    const added = fields.filter((f) => !kept.includes(f));
    setColumnOrder([...kept, ...added]);
  }

  const startDrag = useCallback(
    (event: ReactPointerEvent<HTMLElement>, kind: DragKind, key: string, label: string) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Keep the gesture: no text selection, and DataGrid must not treat it as a header press.
      event.preventDefault();
      event.stopPropagation();

      const grip = event.currentTarget;
      const pointerId = event.pointerId;
      // Capture keeps touch moves on the grip so the page/grid cannot steal the gesture mid-drag.
      try {
        grip.setPointerCapture(pointerId);
      } catch {
        // jsdom / older engines may lack pointer capture.
      }

      dragSession.current = { kind, key, changed: false };
      ghostPosition.current = { x: event.clientX, y: event.clientY };
      setDragGhost({ kind, key, label });
      let lastSwapAt = 0;
      let ended = false;

      const handleMove = (move: PointerEvent) => {
        if (move.pointerId !== pointerId) return;
        // Non-passive listener: block touch scroll while reordering.
        move.preventDefault();
        ghostPosition.current = { x: move.clientX, y: move.clientY };
        // Imperative move — re-rendering the grid on every pointermove would be too chatty.
        if (ghostRef.current) ghostRef.current.style.transform = ghostTransform(move.clientX, move.clientY);

        const session = dragSession.current;
        const container = containerRef.current;
        if (!session || !container) return;

        const hit = dragTargetHit(container, session.kind, move.clientX, move.clientY);
        if (!hit || hit.key === session.key) return;

        // Cooldown applies to columns only: variable widths can oscillate right after a
        // swap. Rows are uniform height, so hysteresis alone is enough — and throttling
        // row swaps drops crossings during a fast drag, leaving the row one slot behind.
        const now = performance.now();
        if (session.kind === 'column' && now - lastSwapAt < REORDER_COOLDOWN_MS) return;

        if (session.kind === 'column') {
          const order = columnOrderRef.current;
          const fromIndex = order.indexOf(session.key);
          const toIndex = order.indexOf(hit.key);
          if (
            !shouldReorderToward({
              pointer: move.clientX,
              targetStart: hit.rect.left,
              targetEnd: hit.rect.right,
              fromIndex,
              toIndex,
            })
          ) {
            return;
          }
          const next = reorder(order, fromIndex, toIndex);
          if (next === order) return;
          columnOrderRef.current = next;
          session.changed = true;
          lastSwapAt = now;
          setColumnOrder(next);
          return;
        }

        const list = localRowsRef.current;
        const fromIndex = list.findIndex((row) => String(row.id) === session.key);
        const toIndex = list.findIndex((row) => String(row.id) === hit.key);
        if (
          !shouldReorderToward({
            pointer: move.clientY,
            targetStart: hit.rect.top,
            targetEnd: hit.rect.bottom,
            fromIndex,
            toIndex,
          })
        ) {
          return;
        }
        const next = reorder(list, fromIndex, toIndex);
        if (next === list) return;
        localRowsRef.current = next;
        session.changed = true;
        setLocalRows(next);
      };

      const handleEnd = (end?: Event) => {
        if (ended) return;
        if (end instanceof PointerEvent && end.pointerId !== pointerId) return;
        // A re-render can drop pointer capture while the button is still held. That is not a drop.
        if (end?.type === 'lostpointercapture') return;
        ended = true;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleEnd);
        window.removeEventListener('pointercancel', handleEnd);
        grip.removeEventListener('lostpointercapture', handleEnd);
        try {
          if (grip.hasPointerCapture(pointerId)) grip.releasePointerCapture(pointerId);
        } catch {
          // Already released or unsupported.
        }
        const session = dragSession.current;
        dragSession.current = null;
        setDragGhost(null);
        if (end?.type === 'pointercancel' || !session?.changed) return;
        if (session.kind === 'column') onColumnsReorder?.(columnOrderRef.current);
        else onRowsReorder?.(localRowsRef.current);
      };

      // `{ passive: false }` so touch `preventDefault` actually blocks scroll.
      window.addEventListener('pointermove', handleMove, { passive: false });
      window.addEventListener('pointerup', handleEnd);
      window.addEventListener('pointercancel', handleEnd);
      grip.addEventListener('lostpointercapture', handleEnd);
    },
    [containerRef, onColumnsReorder, onRowsReorder],
  );

  // The dragged column / row stays in the table (live reorder) but fades out under the ghost.
  const dragSourceSx = useMemo<SxProps<Theme> | undefined>(() => {
    if (!dragGhost) return undefined;
    const key = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(dragGhost.key) : dragGhost.key;
    if (dragGhost.kind === 'column') {
      return {
        [`& .${gridClasses.columnHeader}[data-field="${key}"], & .${gridClasses.cell}[data-field="${key}"]`]: { opacity: 0.35 },
      };
    }
    return { [`& .${gridClasses.row}[data-id="${key}"]`]: { opacity: 0.35 } };
  }, [dragGhost]);

  const ghost = dragGhost ? (
    <Portal>
      <Box
        className="hf-drag-ghost"
        ref={(node: HTMLElement | null) => {
          ghostRef.current = node;
          if (node) {
            node.style.transform = ghostTransform(ghostPosition.current.x, ghostPosition.current.y);
          }
        }}
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: (theme) => theme.zIndex.tooltip + 1,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          border: 1,
          borderColor: 'primary.main',
          bgcolor: 'background.paper',
          boxShadow: 8,
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          color: 'text.primary',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 14, color: 'primary.main' }} />
        {dragGhost.label}
      </Box>
    </Portal>
  ) : null;

  return { columnOrder, localRows, dragging: Boolean(dragGhost), startDrag, dragSourceSx, ghost };
}
